import { Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Grid, Box, IconButton, Link } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  IOrganizationalUnitClientManagers,
  createOrganizationalUnitClientManagers,
  deleteOUClientManagerMember,
  selectedOrganizationalUnitClientManagers,
  ClientDTO,
  ClientContactIdsDto,
  getClientContactByIds,
} from 'wisestep-api';
import {
  WsButton,
  WsListingLoader,
  WsCard,
  CardVariant,
  EmptyTeamsAndUsersIcon,
  WsTypography,
  WsTypoVariants,
  WsDrawer,
  WsTypoColors,
} from 'wisestep-base';

import PersonAvatar from '../../../images/Avatar/Person.svg';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { ClintPageBreadCrumb } from '../../clientDetails/helper/clientDetailsHelper';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';

import ClientManagerList from './ClientManagerList';
import { StyledPaginationBox } from './OrganizationalUnitLayoutNew';

export interface IOrganizationalUnitAllClientManagers {
  id?: string;
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  position?: string;
  clientName?: string;
  markStatus?: boolean;
  workspaceId?: string;
  clientId?: string;
}
interface IAllClientcontactsInUnit {
  organizationalUnitId?: string;
  allClientList: ClientDTO[];
  isClientManagerUnit?: boolean;
  organizationalUnitName?: string;
  isNewUnit?: boolean;
}

const AllClientManagersInUnitList = ({
  organizationalUnitId,
  allClientList,
  isClientManagerUnit,
  organizationalUnitName,
  isNewUnit,
}: IAllClientcontactsInUnit) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [addClientManagerOpen, setaddClientManagerOpen] = useState(false);
  const reload = () => setaddClientManagerOpen(false);
  const [selectedClientManagersList, setselectedClientManagersList] = useState<
    IOrganizationalUnitAllClientManagers[]
  >([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [savedClientContactsIds, setSavedClientContactsIds] = useState<string[]>([]);
  const [allClientUnitList, setAllClientUnitList] = useState<ClientDTO[]>([]);
  const [drawerHeader, setDrawerHeader] = useState<string>('Step 1: Select Clients');
  const setNotification = useNotificationStore((state) => state.setNotification);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await selectedOrganizationalUnitClientManagers(organizationalUnitId ?? '');
      const responseData = response?.data || [];
      const savedIds = responseData.map((item) => item?.clientManagerId);
      setSavedClientContactsIds(savedIds);
      const payload: ClientContactIdsDto = {
        ids: savedIds,
      };
      const selectedClientData = await getClientContactByIds(payload);
      setselectedClientManagersList(selectedClientData?.data || []);
    } catch (error) {
      setselectedClientManagersList([]);
    } finally {
      setLoading(false);
    }
  }, [organizationalUnitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, organizationalUnitId]);

  useEffect(() => {
    setAllClientUnitList(allClientList);
  }, [allClientList]);

  useEffect(() => {
    isNewUnit && setaddClientManagerOpen(true);
  }, [isNewUnit]);

  const updateHeader = (data) => setDrawerHeader(data);

  const data = useMemo(
    () =>
      selectedClientManagersList?.map((data) => ({
        id: data?.id,
        organizationalUnitId: organizationalUnitId,
        name: `${data?.firstName} ${data?.lastName ?? 'NA'}`,
        clientManagerId: data?.id,
        clientName: data?.clientName ?? 'NA',
        clientId: data?.clientId,
      })),
    [organizationalUnitId, selectedClientManagersList]
  );

  const handleDrawerOpen = () => {
    setaddClientManagerOpen(true);
    setDrawerHeader('Step 1: Select Clients');
  };

  const columns: GridColDef[] = [
    {
      headerName: 'Client Contact Name',
      field: 'name',
      flex: 1,
      renderCell: (param) => (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <WsAvatar src={PersonAvatar} variant="circular" />
          </Grid>
          <Grid item>
            <Link
              component={RouterLink}
              underline="none"
              to={`/${param.row.id}/client-contact-details?name=${param.row.clientName}&isChild=true`}
            >
              <WsTypography cursor="pointer" color={WsTypoColors.PRIMARY750}>
                {param.value}
              </WsTypography>
            </Link>
          </Grid>
        </Grid>
      ),
    },
    {
      headerName: 'Client',
      field: 'clientName',
      flex: 1,
      renderCell: (param) => (
        <Link
          component={RouterLink}
          underline="none"
          to={`/client-details?name=${param.row.clientName}&clientId=${
            param.row.clientId
          }&isChild=true&tabIndex=0&breadCrumbList=${getEncodedQueryParams(
            ClintPageBreadCrumb['CLIENTS']
          )}`}
        >
          <WsTypography cursor="pointer" color={WsTypoColors.PRIMARY750}>
            {param.value}
          </WsTypography>
        </Link>
      ),
    },
  ];

  if (!isClientManagerUnit) {
    columns.push({
      headerName: 'Actions',
      field: 'actions',
      renderCell: (param) => (
        <IconButton onClick={() => handleDeleteClientMember(param.row)}>
          <Delete />
        </IconButton>
      ),
    });
  }

  const handleDeleteClientMember = async (rowObj: any) => {
    const response = await selectedOrganizationalUnitClientManagers(organizationalUnitId ?? '');
    const responseData = response?.data || [];
    responseData?.forEach(async (item) => {
      if (
        rowObj.organizationalUnitId === item?.organizationalId &&
        rowObj.clientManagerId === item?.clientManagerId
      ) {
        const payload = item?.value;
        const res = await deleteOUClientManagerMember(payload ?? '');
        if (res.status === 200) {
          setNotification(
            success(`${rowObj.name} is removed from ${organizationalUnitName} successfully!!`)
          );
          fetchData();
        } else {
          setNotification(warning('Something went wrong!!'));
        }
      }
    });
  };

  const addClientToUnit = async (data) => {
    if (organizationalUnitId) {
      const payload: IOrganizationalUnitClientManagers[] = data?.map((element) => ({
        organizationalId: organizationalUnitId,
        clientManagerId: element,
      }));
      const res = await createOrganizationalUnitClientManagers(payload);
      if (res?.status === 200) {
        setNotification(success(`Client Contacts has been added successfully !!`));
        fetchData();
      } else {
        setNotification(warning('Something went wrong!!'));
      }
      setaddClientManagerOpen(false);
    }
  };

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {data.length}
        </WsTypography>
      </Box>
      {data.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(data.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid container mt={2} justifyContent={'space-between'}>
      <Grid container item xs={12}>
        {!isClientManagerUnit && (
          <Box marginLeft="auto" pb={2}>
            <WsButton
              startIcon={<AddIcon />}
              onClick={handleDrawerOpen}
            >{`Add Client Contacts`}</WsButton>
          </Box>
        )}
        <Grid item xs={12}>
          {isLoading ? (
            <WsListingLoader />
          ) : isEmpty(selectedClientManagersList) ? (
            <WsCard
              title={''}
              sx={{ mt: 4 }}
              isButton={false}
              cardType={CardVariant.WITH_LIGHT_BG}
              middleComponent={
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  spacing={3}
                >
                  <EmptyTeamsAndUsersIcon />
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>Its lonely here</WsTypography>
                  <Grid item>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                      Add Client Contacts from your workspace.
                    </WsTypography>
                  </Grid>
                </Grid>
              }
            />
          ) : (
            <>
              {paginationComponent}
              <StyledDataGrid
                hideFooter
                autoHeight
                columns={columns}
                rows={data ?? []}
                page={pageNo}
                pageSize={pageSize}
              />
              {data.length > 10 && paginationComponent}
            </>
          )}
        </Grid>
      </Grid>
      <WsDrawer
        open={addClientManagerOpen}
        onClose={reload}
        title={drawerHeader}
        headerSize="medium"
        size="small"
      >
        <ClientManagerList
          allClientList={allClientUnitList}
          onInput={(data) => addClientToUnit(data)}
          savedContactIds={savedClientContactsIds}
          onUpdateHeader={(data) => updateHeader(data)}
        />
      </WsDrawer>
    </Grid>
  );
};

export default AllClientManagersInUnitList;
